import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  Search,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE = [0.25, 0.46, 0.45, 0.94];

const EMPTY_ARRAY = [];

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'met', label: 'Met' },
  { key: 'shortage', label: 'Shortage' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getComplianceScoreColor(score) {
  if (score >= 80) return { bg: 'bg-emerald-100', text: 'text-emerald-600' };
  if (score >= 50) return { bg: 'bg-amber-100', text: 'text-amber-600' };
  return { bg: 'bg-red-100', text: 'text-red-600' };
}

function getGapDisplay(item) {
  if (item.status === 'excess') {
    return { label: `+${item.gap}`, className: 'text-emerald-600 font-semibold' };
  }
  if (item.status === 'shortage') {
    return { label: `${item.gap}`, className: 'text-red-600 font-semibold' };
  }
  return { label: 'Met', className: 'text-emerald-600 font-medium' };
}

function getStatusBadge(status) {
  switch (status) {
    case 'met':
      return { label: 'Met', variant: 'success' };
    case 'excess':
      return {
        label: 'Excess',
        variant: 'outline',
        className: 'border-emerald-300 text-emerald-700 bg-emerald-50',
      };
    case 'shortage':
      return { label: 'Shortage', variant: 'destructive' };
    default:
      return { label: status, variant: 'secondary' };
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Summary stat cards */
function SummaryBar({ totalRequirements, metCount, shortageCount, complianceScore }) {
  const scoreColor = getComplianceScoreColor(complianceScore);

  const stats = [
    {
      label: 'Total Requirements',
      value: totalRequirements,
      icon: Shield,
      bg: 'bg-blue-100',
      text: 'text-blue-600',
    },
    {
      label: 'Requirements Met',
      value: metCount,
      icon: ShieldCheck,
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
    },
    {
      label: 'Compliance Gaps',
      value: shortageCount,
      icon: ShieldAlert,
      bg: 'bg-red-100',
      text: 'text-red-600',
    },
    {
      label: 'Overall Score',
      value: `${complianceScore}%`,
      icon: ShieldCheck,
      bg: scoreColor.bg,
      text: scoreColor.text,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-6 py-4">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.06 }}
            className="bg-white rounded-xl border border-border p-4 flex items-center gap-3"
          >
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                stat.bg
              )}
            >
              <Icon className={cn('h-5 w-5', stat.text)} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground tracking-tight">
                {stat.value}
              </p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/** Filter bar with search and status toggle */
function ComplianceFilterBar({ searchQuery, setSearchQuery, statusFilter, setStatusFilter }) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-6 py-3 bg-gray-50/80 border-b">
      {/* Search */}
      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search requirements..."
          className="h-8 pl-8 text-sm bg-white"
        />
      </div>

      {/* Status toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.key}
            onClick={() => setStatusFilter(filter.key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
              statusFilter === filter.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Sortable column header */
function SortableHeader({ label, sortKey, currentSort, onSort, className }) {
  const isActive = currentSort.key === sortKey;
  const isAsc = isActive && currentSort.direction === 'asc';

  return (
    <button
      onClick={() => onSort(sortKey)}
      className={cn(
        'flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors group',
        className
      )}
    >
      {label}
      <span className="flex flex-col">
        {isActive ? (
          isAsc ? (
            <ChevronUp className="h-3 w-3 text-gray-700" />
          ) : (
            <ChevronDown className="h-3 w-3 text-gray-700" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 text-gray-300 group-hover:text-gray-500 transition-colors" />
        )}
      </span>
    </button>
  );
}

/** Single requirement row */
function RequirementRow({ item, rowIndex }) {
  const isMet = item.status === 'met' || item.status === 'excess';
  const gapDisplay = getGapDisplay(item);
  const statusBadge = getStatusBadge(item.status);

  return (
    <motion.tr
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: EASE, delay: Math.min(rowIndex * 0.02, 0.3) }}
      className="group border-b border-gray-100 last:border-b-0 hover:bg-muted/50 transition-colors"
    >
      {/* Status icon */}
      <td className="pl-6 pr-2 py-3 w-10">
        {isMet ? (
          <CheckCircle className="h-4.5 w-4.5 text-emerald-500" />
        ) : (
          <AlertTriangle className="h-4.5 w-4.5 text-red-500" />
        )}
      </td>

      {/* Requirement name */}
      <td className="px-3 py-3">
        <span className="text-sm font-medium text-gray-900">{item.name}</span>
      </td>

      {/* Category */}
      <td className="px-3 py-3">
        {item.category ? (
          <Badge
            variant="outline"
            className="text-[11px] px-1.5 py-0 h-5 border-gray-200 text-gray-600 bg-gray-50"
          >
            {item.category}
          </Badge>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>

      {/* On-Site */}
      <td className="px-3 py-3 text-center">
        <span className="text-sm tabular-nums text-gray-700">{item.onSite ?? 0}</span>
      </td>

      {/* Required */}
      <td className="px-3 py-3 text-center">
        <span className="text-sm tabular-nums text-gray-700">{item.required ?? 0}</span>
      </td>

      {/* Gap */}
      <td className="px-3 py-3 text-center">
        <span className={cn('text-sm tabular-nums', gapDisplay.className)}>
          {gapDisplay.label}
        </span>
      </td>

      {/* Status badge */}
      <td className="px-3 py-3 pr-6">
        <Badge
          variant={statusBadge.variant}
          className={cn('text-[11px]', statusBadge.className)}
        >
          {statusBadge.label}
        </Badge>
      </td>
    </motion.tr>
  );
}

/** Loading skeleton rows */
function LoadingSkeleton() {
  return (
    <tbody>
      {[...Array(6)].map((_, rowIdx) => (
        <tr key={rowIdx} className="border-b border-gray-100">
          <td className="pl-6 pr-2 py-3">
            <Skeleton className="h-4.5 w-4.5 rounded-full" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-4 w-40" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-5 w-20 rounded-md" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-4 w-8 mx-auto" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-4 w-8 mx-auto" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-4 w-10 mx-auto" />
          </td>
          <td className="px-3 py-3 pr-6">
            <Skeleton className="h-5 w-16 rounded-md" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

/** Empty state */
function EmptyState({ hasFilters }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <Shield className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">
        {hasFilters ? 'No matching requirements' : 'No compliance requirements configured'}
      </h3>
      <p className="text-sm text-gray-500 text-center max-w-xs">
        {hasFilters
          ? 'Try adjusting your search or filter to find what you are looking for.'
          : 'Compliance requirements will appear here once they have been configured for this site.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LiveComplianceView({ gapAnalysis, gapAnalysisCounts, gapLoading }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'name', direction: 'asc' });

  // Stabilize the array reference so useMemo deps do not re-trigger on every render
  const safeGapAnalysis = useMemo(() => gapAnalysis || EMPTY_ARRAY, [gapAnalysis]);
  const { shortageCount = 0, metCount = 0 } = gapAnalysisCounts || {};

  // Compliance score
  const complianceScore = useMemo(() => {
    if (safeGapAnalysis.length === 0) return 100;
    return Math.round((metCount / safeGapAnalysis.length) * 100);
  }, [safeGapAnalysis, metCount]);

  // Handle sort toggle
  const handleSort = useCallback(
    (key) => {
      setSort((prev) => {
        if (prev.key === key) {
          return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
      });
    },
    []
  );

  // Filtered and sorted data
  const filteredData = useMemo(() => {
    let items = [...safeGapAnalysis];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.category && item.category.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter === 'met') {
      items = items.filter((item) => item.status === 'met' || item.status === 'excess');
    } else if (statusFilter === 'shortage') {
      items = items.filter((item) => item.status === 'shortage');
    }

    // Sort
    items.sort((a, b) => {
      let comparison = 0;
      switch (sort.key) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'gap': {
          const gapA = typeof a.gap === 'number' ? a.gap : 0;
          const gapB = typeof b.gap === 'number' ? b.gap : 0;
          comparison = gapA - gapB;
          break;
        }
        default:
          comparison = 0;
      }
      return sort.direction === 'asc' ? comparison : -comparison;
    });

    return items;
  }, [safeGapAnalysis, searchQuery, statusFilter, sort]);

  const hasFilters = searchQuery.trim() !== '' || statusFilter !== 'all';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Summary bar */}
      <SummaryBar
        totalRequirements={safeGapAnalysis.length}
        metCount={metCount}
        shortageCount={shortageCount}
        complianceScore={complianceScore}
      />

      <Separator />

      {/* Filter bar */}
      <ComplianceFilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {/* Table area */}
      <div className="flex-1 overflow-auto">
        {gapLoading ? (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <tr>
                <th className="pl-6 pr-2 py-2.5 w-10" />
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requirement
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On-Site
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Required
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gap
                </th>
                <th className="px-3 py-2.5 pr-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <LoadingSkeleton />
          </table>
        ) : safeGapAnalysis.length === 0 && !hasFilters ? (
          <EmptyState hasFilters={false} />
        ) : filteredData.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <table className="w-full border-collapse">
            {/* Column headers */}
            <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <tr>
                <th className="pl-6 pr-2 py-2.5 w-10" />
                <th className="px-3 py-2.5 text-left">
                  <SortableHeader
                    label="Requirement"
                    sortKey="name"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                </th>
                <th className="px-3 py-2.5 text-left">
                  <SortableHeader
                    label="Category"
                    sortKey="category"
                    currentSort={sort}
                    onSort={handleSort}
                  />
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On-Site
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Required
                </th>
                <th className="px-3 py-2.5 text-center">
                  <SortableHeader
                    label="Gap"
                    sortKey="gap"
                    currentSort={sort}
                    onSort={handleSort}
                    className="justify-center"
                  />
                </th>
                <th className="px-3 py-2.5 pr-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>

            {/* Requirement rows */}
            <tbody>
              {filteredData.map((item, idx) => (
                <RequirementRow key={item.id || idx} item={item} rowIndex={idx} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
